import {
  type CampaignCalendarConnectorSetupQuery,
  useCampaignCalendarConnectorSetupQuery,
  useCampaignCalendarDestinationPermissionsQuery,
  useCampaignCalendarEngagementSetupQuery,
} from "src/graphql";
import {
  type GovernedRefetchFn,
  useGovernedRefetchInterval,
} from "src/hooks/use-governed-refetch-interval";

import {
  deriveCalendarSendsState,
  deriveEngagementStep,
  derivePermissionsStep,
  deriveSendsStep,
} from "src/pages/agents/calendar/setup/calendar-setup-status";

const CONNECTOR_POLL_MS = 60_000;
const CONNECTOR_MAX_POLL_MS = 5 * 60_000;
const PERMISSIONS_STALE_MS = 60 * 60_000;

type Connectors =
  CampaignCalendarConnectorSetupQuery["campaignCalendarConnectorSetup"];

/** Polls connector status until `isSettled`, since nothing pushes Fivetran or build progress. */
export function useConnectorSetupRefetchInterval({
  maxMs,
  isSettled,
}: {
  maxMs: number;
  isSettled: (connectors: Connectors) => boolean;
}): GovernedRefetchFn<CampaignCalendarConnectorSetupQuery> {
  const { refetchInterval } =
    useGovernedRefetchInterval<CampaignCalendarConnectorSetupQuery>({
      baseMs: CONNECTOR_POLL_MS,
      maxMs,
      compareKey: (data) =>
        data?.campaignCalendarConnectorSetup.map((connector) => [
          connector.id,
          connector.connectionState,
          connector.hasCalendarData,
        ]),
    });
  return (data, query) =>
    data && isSettled(data.campaignCalendarConnectorSetup)
      ? false
      : refetchInterval(data, query);
}

export function useCalendarConnectorSetup() {
  const refetchInterval = useConnectorSetupRefetchInterval({
    maxMs: CONNECTOR_MAX_POLL_MS,
    // One published connection makes the calendar readable, but other imports still need polling.
    isSettled: (connectors) =>
      deriveSendsStep(connectors) === "done" &&
      connectors.every((connector) => connector.hasCalendarData),
  });
  const { data, refetch, isFetching } = useCampaignCalendarConnectorSetupQuery(
    undefined,
    {
      suspense: true,
      // Only a first load reaches the error boundary; a failed poll keeps the last status until the next poll.
      useErrorBoundary: (_error, query) => query.state.data === undefined,
      refetchInterval,
    },
  );
  const connectors: Connectors = data?.campaignCalendarConnectorSetup ?? [];
  return {
    connectors,
    sendsState: deriveCalendarSendsState(connectors),
    sendsStep: deriveSendsStep(connectors),
    refetch,
    isFetching,
  };
}

export function useCalendarEngagementSetup(enabled: boolean) {
  const { data, isError, refetch, isFetching } =
    useCampaignCalendarEngagementSetupQuery(undefined, { enabled });
  return {
    step: deriveEngagementStep({
      data: data?.campaignCalendarEngagementSetup,
      isError,
    }),
    refetch,
    isFetching,
  };
}

/** Each check makes live calls to the email provider, so it reruns only on "Check again" or a visit an hour after the last check. */
export function useCalendarPermissionsSetup(enabled: boolean) {
  const { data, isError, refetch, isFetching } =
    useCampaignCalendarDestinationPermissionsQuery(undefined, {
      enabled,
      staleTime: PERMISSIONS_STALE_MS,
    });
  return {
    step: derivePermissionsStep({
      data: data?.checkDestinationPermissions,
      isError,
    }),
    refetch,
    isFetching,
  };
}
